/**
 * Форматирование значений для интерфейса.
 *
 * Всё в одном месте, чтобы одна и та же длительность не выглядела на двух
 * экранах по-разному.
 */

/** Длительность в секундах → `4:32`. `null` — прочерк, а не ноль. */
export function duration(seconds: number | null | undefined): string {
  if (seconds === null || seconds === undefined) return '—';
  const total = Math.max(0, Math.round(seconds));
  const hours = Math.floor(total / 3600);
  const minutes = Math.floor((total % 3600) / 60);
  const secs = total % 60;
  if (hours > 0) {
    return `${hours}:${String(minutes).padStart(2, '0')}:${String(secs).padStart(2, '0')}`;
  }
  return `${minutes}:${String(secs).padStart(2, '0')}`;
}

const dateTimeFormat = new Intl.DateTimeFormat('ru-RU', {
  day: '2-digit',
  month: 'short',
  year: 'numeric',
  hour: '2-digit',
  minute: '2-digit',
});

/**
 * Короткая дата для таблиц: «07.09.2026, 11:43».
 *
 * Полная («07 сент. 2026 г., 11:43») в узкой колонке разваливается на
 * несколько строк и растягивает всю строку таблицы.
 */
const compactFormat = new Intl.DateTimeFormat('ru-RU', {
  day: '2-digit',
  month: '2-digit',
  year: 'numeric',
  hour: '2-digit',
  minute: '2-digit',
});

const timeFormat = new Intl.DateTimeFormat('ru-RU', {
  hour: '2-digit',
  minute: '2-digit',
  second: '2-digit',
});

/** RFC3339 → `04 сен 2026, 15:10`. Время местное: звонки смотрят на месте. */
export function dateTime(iso: string | null | undefined): string {
  if (!iso) return '—';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '—';
  return dateTimeFormat.format(date);
}

export function dateTimeCompact(iso: string | null | undefined): string {
  if (!iso) return '—';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '—';
  return compactFormat.format(date);
}

export function time(iso: string | null | undefined): string {
  if (!iso) return '—';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '—';
  return timeFormat.format(date);
}

/** Дата для полей `from`/`to` и `activeFrom`: бэкенд ждёт `2006-01-02`. */
export function isoDate(date: Date): string {
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${date.getFullYear()}-${month}-${day}`;
}

/** Инициалы для аватара: две первые буквы слов. */
export function initials(name: string): string {
  return name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((word) => word[0]?.toUpperCase() ?? '')
    .join('');
}

/** Номер телефона как есть — форматировать чужие форматы себе дороже. */
export function phone(value: string | null | undefined): string {
  return value && value.trim() ? value : '—';
}

/**
 * Доля 0..1 → «93%». `null` — прочерк.
 *
 * Прочерк здесь не украшение: `null` означает пустой знаменатель — «не было
 * ни одного звонка», а не «ни разу не ответили». Нарисовав 0%, интерфейс
 * обвинил бы людей в том, чего не было.
 */
export function percent(value: number | null | undefined, digits = 0): string {
  if (value === null || value === undefined) return '—';
  return `${(value * 100).toFixed(digits)}%`;
}

/** Балл и прочие дробные: `null` — прочерк, не ноль. */
export function decimal(value: number | null | undefined, digits = 1): string {
  if (value === null || value === undefined) return '—';
  return value.toFixed(digits);
}

/** Целое с разделителем тысяч: 12 480 читается, 12480 — нет. */
export function count(value: number | null | undefined): string {
  if (value === null || value === undefined) return '—';
  return value.toLocaleString('ru-RU');
}

/**
 * Подпись периода отчёта.
 *
 * Считается **в поясе тенанта**, взятом из ответа: границы приходят готовыми
 * ровно затем, чтобы клиент их не пересчитывал. Сутки «сегодня» для Алматы
 * начинаются в 19:00 UTC вчера, и подпись, собранная в поясе браузера,
 * разошлась бы с цифрами на границе месяца.
 *
 * `to` не включается в период, поэтому в подписи показываем последний
 * вошедший день: «1 — 30 сентября», а не «1 сентября — 1 октября».
 */
export function periodLabel(period: { from: string; to: string; timezone: string }): string {
  const from = new Date(period.from);
  const lastIncluded = new Date(new Date(period.to).getTime() - 1);
  if (Number.isNaN(from.getTime()) || Number.isNaN(lastIncluded.getTime())) return '—';

  const format = (date: Date, withYear: boolean) =>
    new Intl.DateTimeFormat('ru-RU', {
      day: 'numeric',
      month: 'short',
      year: withYear ? 'numeric' : undefined,
      timeZone: period.timezone || undefined,
    }).format(date);

  const sameDay = format(from, true) === format(lastIncluded, true);
  return sameDay ? format(from, true) : `${format(from, false)} — ${format(lastIncluded, true)}`;
}

/** Длина периода в сутках — для подписи сравнения «к предыдущим N дням». */
export function periodDays(period: { from: string; to: string }): number {
  const ms = new Date(period.to).getTime() - new Date(period.from).getTime();
  if (!Number.isFinite(ms) || ms <= 0) return 0;
  return Math.max(1, Math.round(ms / 86_400_000));
}

/** «5 дней» / «1 день» / «22 дня» — иначе подпись сравнения звучит по-машинному. */
export function plural(value: number, one: string, few: string, many: string): string {
  const mod10 = value % 10;
  const mod100 = value % 100;
  if (mod10 === 1 && mod100 !== 11) return `${value} ${one}`;
  if (mod10 >= 2 && mod10 <= 4 && (mod100 < 12 || mod100 > 14)) return `${value} ${few}`;
  return `${value} ${many}`;
}

/**
 * Сколько времени прошло — словами.
 *
 * «3 минуты назад» отвечает на вопрос «работает ли сейчас» сразу, а
 * «12.09.2026, 14:30» требует считать в уме, да ещё и знать, какое сегодня
 * число. Там, где важна свежесть, а не момент, нужна именно эта форма.
 */
export function sinceNow(iso: string | null | undefined): string {
  if (!iso) return '—';
  const ms = Date.now() - new Date(iso).getTime();
  if (!Number.isFinite(ms)) return '—';
  if (ms < 0) return 'только что';

  const minutes = Math.floor(ms / 60_000);
  if (minutes < 1) return 'только что';
  if (minutes < 60) return `${plural(minutes, 'минуту', 'минуты', 'минут')} назад`;

  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${plural(hours, 'час', 'часа', 'часов')} назад`;

  const days = Math.floor(hours / 24);
  return `${plural(days, 'день', 'дня', 'дней')} назад`;
}
